import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Converts an ISO 3166-1 alpha-2 country code to flag emoji.
 * @param {string} iso2 - Two-letter country code (e.g. "US", "ES")
 * @returns {string|null} Flag emoji or null if invalid
 */
export function isoToFlagEmoji(iso2) {
  if (!iso2 || typeof iso2 !== 'string' || iso2.length !== 2) return null;
  const code = iso2.toUpperCase();
  if (code < 'A' || code > 'Z') return null;
  return [...code].map((c) => String.fromCodePoint(127397 + c.charCodeAt(0))).join('');
}

/**
 * Renders country value with optional flag (when value is a 2-letter ISO code).
 * @param {string|null} value - Country code or label (e.g. "US", "Spain")
 * @returns {React.ReactNode} JSX for display
 */
CountryWithFlag.propTypes = {
  value: PropTypes.string.isRequired
};
export function CountryWithFlag({value}) {
  const displayValue = value || null;
  if (!displayValue) return '—';
  const flag = isoToFlagEmoji(displayValue.length === 2 ? displayValue : null);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {flag && (
        <Box component="span" sx={{ fontSize: '1.25rem', lineHeight: 1 }} aria-hidden>
          {flag}
        </Box>
      )}
      <Box component="span">{displayValue}</Box>
    </Box>
  );
}

/**
 * MUI Chip color for an accountPlan.subscriptionStatus value.
 * Note: the real status value is "canceled" (one L, Stripe's spelling).
 * @param {string} status - subscriptionStatus value
 * @returns {'success'|'error'|'default'} MUI color prop
 */
export function subscriptionStatusColor(status) {
  if (status === 'active') return 'success';
  if (status === 'canceled' || status === 'unpaid' || status === 'past_due') return 'error';
  return 'default';
}

const completeProfiles = (accounts) => {
  accounts.forEach((account) => {
    if (!account.completedProfile) {
      if (!account.profilePicture) {
        account.completedProfile = 'Profile picture';
      }
      if (!account.bio) {
        account.completedProfile = typeof account.completedProfile === 'string' ? account.completedProfile + ', Bio' : 'Bio';
      }

      if (
        !account.socialMedia ||
        (!account.socialMedia.instagram &&
          !account.socialMedia.facebook &&
          !account.socialMedia.linkedin &&
          !account.socialMedia.tiktok &&
          !account.socialMedia.twitter)
      ) {
        account.completedProfile =
          typeof account.completedProfile === 'string' ? account.completedProfile + ', Social Media' : 'Social Media';
      }

      if (!account.completedProfile) {
        account.completedProfile = true;
      }
    } else {
      account.completedProfile = true;
    }
  });
  return accounts;
};

export default completeProfiles;
